import { useState, useRef, useEffect } from "react";
import { Camera, Upload, Check, Loader2, X, FolderPlus, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function PhotoScan() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState([]); // [{term, definition, exists}]
  const [selected, setSelected] = useState({});
  const [adding, setAdding] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [existingTermSet, setExistingTermSet] = useState(new Set());
  const inputRef = useRef(null);

  // Load categories and existing words on mount so we can show dedupe hints
  useEffect(() => {
    (async () => {
      try {
        const [cats, words] = await Promise.all([
          api.get("/categories"),
          api.get("/words"),
        ]);
        setCategories(cats.data || []);
        const set = new Set((words.data || []).map((w) => (w.term || "").trim().toLowerCase()));
        setExistingTermSet(set);
      } catch { /* user might be unauth or offline — ignore */ }
    })();
  }, []);

  const onPick = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setExtracted([]);
    setSelected({});
  };

  const onUpload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/words/extract-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const raw = r.data.words || [];
      // Flag duplicates against the user's existing glossary AND against duplicates inside the same scan
      const seen = new Set();
      const annotated = raw.map((w) => {
        const key = (w.term || "").trim().toLowerCase();
        const isExisting = existingTermSet.has(key);
        const isInBatchDup = seen.has(key);
        if (key) seen.add(key);
        return { term: (w.term || "").trim(), definition: (w.definition || "").trim(), exists: isExisting, batchDup: isInBatchDup };
      }).filter((w) => w.term && w.definition);
      setExtracted(annotated);
      // Default: tick everything that's not already in the glossary or a same-batch duplicate
      const s = {};
      annotated.forEach((w, i) => { s[i] = !w.exists && !w.batchDup; });
      setSelected(s);
      if (annotated.length === 0) toast.error("No clear term/definition pairs found in this image.");
      else {
        const dupes = annotated.filter((w) => w.exists || w.batchDup).length;
        if (dupes > 0) toast.success(`Found ${annotated.length} terms — ${dupes} already in your glossary (auto-unticked).`);
        else toast.success(`Found ${annotated.length} terms.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed.");
    } finally {
      setLoading(false);
    }
  };

  const removeRow = (i) => {
    setExtracted((arr) => arr.filter((_, idx) => idx !== i));
    setSelected((s) => {
      const next = {};
      Object.keys(s).forEach((k) => {
        const ki = Number(k);
        if (ki < i) next[ki] = s[k];
        else if (ki > i) next[ki - 1] = s[k];
      });
      return next;
    });
  };

  const createCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setCreatingCat(true);
    try {
      const r = await api.post("/categories", { name });
      setCategories((c) => [...c, r.data]);
      setCategoryId(r.data.id);
      setNewCatName("");
      toast.success(`Category "${name}" created.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create category.");
    } finally {
      setCreatingCat(false);
    }
  };

  const addAll = async () => {
    const picked = extracted.filter((_, i) => selected[i]);
    if (picked.length === 0) { toast.error("Tick at least one term to add."); return; }
    setAdding(true);
    try {
      const r = await api.post("/words/bulk", {
        words: picked.map((w) => ({ term: w.term, definition: w.definition })),
        category_id: categoryId || null,
      });
      const added = r.data.added || 0;
      const skipped = r.data.skipped_duplicates || 0;
      if (added > 0) {
        toast.success(`Added ${added} word${added === 1 ? "" : "s"} to your glossary${skipped ? ` — ${skipped} duplicates skipped.` : "."}`);
      } else if (skipped > 0) {
        toast.message(`All ${skipped} words were already in your glossary.`);
      }
      // Refresh existing set so further scans dedupe correctly
      const refreshed = await api.get("/words");
      setExistingTermSet(new Set((refreshed.data || []).map((w) => (w.term || "").trim().toLowerCase())));
      setExtracted([]); setSelected({}); setFile(null); setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add words.");
    } finally {
      setAdding(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-5xl">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Photo Scan</p>
        <h1 className="font-display text-4xl font-black mt-1">Snap a textbook page</h1>
        <p className="text-base font-medium text-slate-600 mt-2">Upload a photo. AI transcribes the terms + definitions <strong>verbatim</strong> from your notes. Review, pick a category, then add to your glossary.</p>
      </header>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT — uploader */}
        <div>
          <label
            htmlFor="scan-file"
            data-testid="scan-dropzone"
            className="block cursor-pointer border-4 border-dashed border-slate-900 rounded-lg p-10 text-center transition-colors hover:bg-sky-100"
            style={{ background: "#7DD3FC33" }}
          >
            {preview ? (
              <img src={preview} alt="preview" className="mx-auto max-h-72 rounded-md border-2 border-slate-900"/>
            ) : (
              <>
                <Camera className="w-10 h-10 mx-auto" strokeWidth={2.5}/>
                <p className="font-display text-xl font-bold mt-3">Tap to select photo</p>
                <p className="text-sm text-slate-600 mt-1">JPG / PNG / WebP — clear, well-lit pages work best.</p>
              </>
            )}
            <input ref={inputRef} id="scan-file" data-testid="scan-file-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e)=>onPick(e.target.files?.[0])}/>
          </label>
          {file && (
            <div className="flex gap-3 mt-4">
              <button onClick={onUpload} disabled={loading} data-testid="scan-extract-btn" className="nb-btn-primary inline-flex items-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> : <Upload className="w-4 h-4" strokeWidth={2.5}/>}
                {loading ? "Extracting…" : "Extract terms"}
              </button>
              <button onClick={() => { setFile(null); setPreview(null); setExtracted([]); }} className="nb-btn-secondary">Reset</button>
            </div>
          )}
        </div>

        {/* RIGHT — review */}
        <div>
          <h2 className="font-display text-2xl font-bold mb-3">Extracted vocabulary</h2>
          {extracted.length === 0 ? (
            <div className="nb-card p-8 text-center">
              <p className="text-sm text-slate-600">Pick an image and tap Extract to see results here.</p>
            </div>
          ) : (
            <>
              {/* Category picker */}
              <div className="nb-card p-3 mb-3 bg-yellow-50" data-testid="scan-category-picker">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-700 flex items-center gap-1.5">
                  <FolderPlus className="w-3.5 h-3.5" strokeWidth={2.5}/> Add these to category
                </label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    data-testid="scan-category-select"
                    className="nb-input flex-1 min-w-[160px]"
                  >
                    <option value="">Uncategorised</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    placeholder="…or create a new category"
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    data-testid="scan-new-category-input"
                    className="nb-input flex-1 text-sm"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createCategory(); } }}
                  />
                  <button
                    onClick={createCategory}
                    disabled={!newCatName.trim() || creatingCat}
                    data-testid="scan-create-category-btn"
                    className="nb-btn-secondary text-sm whitespace-nowrap"
                  >
                    {creatingCat ? "Creating…" : "Create"}
                  </button>
                </div>
              </div>

              <ul className="space-y-3">
                {extracted.map((w, i) => {
                  const dup = w.exists || w.batchDup;
                  return (
                    <li key={i} className={`nb-card-sm p-3 flex items-start gap-3 ${dup ? "bg-slate-50" : "bg-white"}`} data-testid={`extracted-${i}`}>
                      <input
                        type="checkbox"
                        checked={!!selected[i]}
                        onChange={(e) => setSelected((s) => ({ ...s, [i]: e.target.checked }))}
                        data-testid={`extracted-check-${i}`}
                        className="mt-1 w-5 h-5 border-2 border-slate-900 accent-yellow-300"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold flex items-center gap-2 flex-wrap">
                          <span>{w.term}</span>
                          {dup && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                              <AlertCircle className="w-3 h-3" strokeWidth={2.5}/>
                              {w.exists ? "Already in glossary" : "Duplicate in scan"}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{w.definition}</div>
                      </div>
                      <button
                        onClick={() => removeRow(i)}
                        data-testid={`extracted-remove-${i}`}
                        aria-label={`Remove ${w.term}`}
                        title="Remove from list"
                        className="shrink-0 p-1.5 rounded-md border-2 border-slate-900 bg-white hover:bg-red-50"
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={2.5}/>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={addAll} disabled={adding || selectedCount === 0} data-testid="scan-add-all-btn" className="nb-btn-primary inline-flex items-center gap-2">
                  <Check className="w-4 h-4" strokeWidth={2.5}/>
                  {adding ? "Adding…" : `Add ${selectedCount} to glossary`}
                </button>
                <button onClick={() => setSelected(Object.fromEntries(extracted.map((_, i) => [i, true])))} className="nb-btn-secondary text-xs">Tick all</button>
                <button onClick={() => setSelected({})} className="nb-btn-secondary text-xs">Untick all</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
